/**
 * Build-time configuration. Every host and every tunable is a `VITE_*`
 * variable with a documented default — no endpoint and no magic number lives at
 * a call site.
 */

function env(name: string, fallback: string): string {
  const v = import.meta.env?.[name as keyof ImportMetaEnv] as string | undefined;
  return v && v.length > 0 ? v : fallback;
}

declare const __BUILD_SHA__: string;

/**
 * The console build stamp `src/main.ts` prints at load. `scripts/deploy-pages.sh`
 * greps the entry chunk for `arrowchess-live build <sha>`, so the `define`
 * literal has to sit inside this template, where the minifier folds it into one
 * string; it does not inline a SHA variable into a template elsewhere in the
 * chunk (measured: that shipped `${ur}` and no stamp). `"dev"` covers a build
 * with no `define` (the Pages Functions bundle, which imports this module).
 */
export const BUILD_STAMP = `arrowchess-live build ${typeof __BUILD_SHA__ !== "undefined" ? __BUILD_SHA__ : "dev"}`;

/**
 * Git commit SHA baked in at build time by `vite.config.ts` (`define: { __BUILD_SHA__ }`),
 * read out of `BUILD_STAMP` so the define is read in one place. Attached to client
 * error reports.
 */
export const BUILD_SHA: string = BUILD_STAMP.replace(/^arrowchess-live build /, "");

/**
 * Anonymous client error reporting kill switch (`VITE_CLIENT_ERRORS`).
 *
 * Enabled by default; set to `"0"` at build time to disable all error listeners
 * and reports. When disabled, no error telemetry is installed.
 */
export const CLIENT_ERRORS: boolean = env("VITE_CLIENT_ERRORS", "") !== "0";

/**
 * The one canonical origin this site is addressed by — no trailing slash.
 *
 * Every absolute URL the site mints (share links, canonical/OG metadata, a
 * printed address in a page) composes this rather than repeating a hostname, so
 * a host move is one edit. It is a build-time value, not `location.origin`,
 * because the same URL has to be right when it is written into markup rendered
 * on a preview deploy or driven by a headless check, where `location.origin` is
 * whatever host happened to serve the bundle.
 *
 * The apex, not `live.arrowchess.com`: the site moved to `arrowchess.com` and
 * `live.`/`www.` 301 here (`public/_redirects`, checked by
 * `src/test/headers.test.ts`). `scripts/check-editorial.mjs` runs under plain
 * `node` and cannot import this module, so it carries the literal with an env
 * override and that same test is the agreement check.
 */
export const SITE_ORIGIN = env("VITE_SITE_ORIGIN", "https://arrowchess.com").replace(/\/+$/, "");

/**
 * relay-distributor — `chessstreamlive.broadcast.v1.Broadcast` over grpc-web.
 *
 * csl-a1 behind Cloudflare (orange cloud, gRPC on): Caddy on :443 → the
 * distributor on loopback (`ops/csl-a1/`, #3258). The default and the CSP
 * `connect-src` in `public/_headers` name the same origin and are checked
 * against each other by `config.csp.test.ts`. Installed app builds still use the
 * Cloud Run service URL; rollback for the web is a Pages redeploy with
 * `VITE_API_HOST=https://csl-relay-distributor-staging-647722354406.europe-west2.run.app`
 * plus that origin back in `public/_headers` and `engine-selftest.html`.
 */
export const API_HOST = env("VITE_API_HOST", "https://live-a1.arrowchess.com");

/** content-service behind the Studio tunnel: Catalog reads over unary grpc-web, and the calendar GET when {@link CALENDAR_TRANSPORT} opts in. */
export const CATALOG_HOST = env("VITE_CATALOG_HOST", "https://catalog.arrowchess.com");

/**
 * The accounts plane's Edge Functions origin — the Supabase project whose mailed
 * links this site answers (`https://<ref>.supabase.co`, no path, no trailing slash).
 *
 * Today one page uses it: `/account/email-change/cancel` POSTs the single-use
 * cancel token to `email-change-execute` here (#3549). Hosted Edge Functions turn a
 * GET `text/html` response into `text/plain` when there is no custom domain, so the
 * function's own confirmation page loses its button; the owner's ruling is a
 * first-party page on this site instead, which the function's
 * `CSL_EMAIL_CHANGE_CANCEL_URL` points at.
 *
 * ONE project per build. `public/_headers` is a static file, so its `connect-src`
 * names exactly this origin and `config.csp.test.ts` holds the two in agreement.
 * The default is csl-staging (`ops/csl/envs/staging.env` `CSL_SUPABASE_PROJECT_REF`),
 * the project that stays on Pro for the iOS submission (#3286); csl-dev's hosted
 * e2e POSTs the function directly and does not go through this page.
 */
export const ACCOUNTS_FUNCTIONS_ORIGIN = env("VITE_ACCOUNTS_FUNCTIONS_ORIGIN", "https://jjcvzjkarreujfsegerk.supabase.co");

/** `email-change-execute`'s public address — its unauthenticated cancel door (`?cancel_token=`, POST). */
export const EMAIL_CHANGE_CANCEL_ENDPOINT = `${ACCOUNTS_FUNCTIONS_ORIGIN}/functions/v1/email-change-execute`;

/**
 * How the calendar (`BroadcastList`) is read from {@link CATALOG_HOST}.
 *
 * `grpc-web` (the default) is the `Catalog.ListBroadcasts` POST every deployed
 * backend serves. `http-get` is the cacheable `GET /v1/calendar.pb` (#2580),
 * which only a content-service built from #2586 or later serves. The switch is
 * a build-time opt-in so merging the web change and deploying Pages cannot
 * point browsers at a route the backend has not been promoted with: flip it
 * (`VITE_CALENDAR_TRANSPORT=http-get`) only after the backend deploy and the
 * route smoke in `docs/ops/calendar-http-get-2026-09.md`. Any other value is
 * the default.
 */
export const CALENDAR_TRANSPORT: "grpc-web" | "http-get" =
  env("VITE_CALENDAR_TRANSPORT", "grpc-web") === "http-get" ? "http-get" : "grpc-web";

/**
 * An OPTIONAL filter on the calendar, `VITE_TOUR_ID="id,id"`.
 *
 * The site is not about one event. The homepage is a grid of every tournament
 * on the wire whose winner is not yet decided (`src/tours.ts`), so the shipped
 * default is EMPTY — "no filter" — and `inTours` keeps everything.
 *
 * It stays a LIST, and stays settable, because one event is not one Lichess
 * tour: the GCL 2026 ran as a Preliminary Stage (`B6gJ5CBc`) plus Finals
 * (`uacsaXZb`), and a single-id filter silently hid every Finals round — the
 * rounds were on the wire and the site dropped them. A deploy that genuinely is
 * about one event (a league's own microsite) sets the variable; the apex does
 * not.
 */
export function parseTourIds(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const TOUR_IDS: readonly string[] = parseTourIds(env("VITE_TOUR_ID", ""));

/** First configured tour, for a deploy that pinned one. `""` — the default — when unfiltered. */
export const TOUR_ID: string = TOUR_IDS[0] ?? "";

/** How far back `ListBroadcasts` should reach for recently-finished rounds. */
export const FINISHED_WINDOW_SECS = Number.parseInt(env("VITE_FINISHED_WINDOW_SECS", "86400"), 10);

export interface BoardScoring {
  whiteWin: number;
  blackWin: number;
  draw: number;
}

/**
 * Board-point weights, `VITE_SCORING="white,black,draw"`.
 *
 * The GCL pays a White win 3, a Black win 4 and a draw 1 — the asymmetry is the
 * league's signature rule, meant to reward playing for a win with the black
 * pieces. It is config, not a literal, because the same site must be able to
 * show a 1/½ event without a code change.
 */
export const SCORING: BoardScoring = parseScoring(env("VITE_SCORING", "3,4,1"));

/**
 * The game-point scale every chess result is reported on: a win 1, a draw ½.
 *
 * Deliberately NOT config and NOT `SCORING`. A match tally of "2½–1½" is the
 * arithmetic of the results themselves, so it is true at any event — a league's
 * board-point scale ({@link SCORING}) is a separate, tour-gated quantity that
 * only {@link roundScoreSides} may apply.
 */
export const GAME_POINTS: BoardScoring = { whiteWin: 1, blackWin: 1, draw: 0.5 };

function parseScoring(raw: string): BoardScoring {
  const parts = raw.split(",").map((p) => Number.parseFloat(p.trim()));
  const [w, b, d] = parts;
  if (parts.length !== 3 || w === undefined || b === undefined || d === undefined || !Number.isFinite(w) || !Number.isFinite(b) || !Number.isFinite(d)) {
    console.warn(`[config] VITE_SCORING="${raw}" is not "white,black,draw" — falling back to the GCL scale`);
    return { whiteWin: 3, blackWin: 4, draw: 1 };
  }
  return { whiteWin: w, blackWin: b, draw: d };
}

export interface MatchScoring {
  win: number;
  draw: number;
  loss: number;
}

/**
 * Match-point weights, `VITE_MATCH_POINTS="win,draw,loss"`.
 *
 * A DECLARED MIRROR of the standings engine's per-edition rules block — see the
 * `mirror-of:` marker on the definition below. The GCL's round robin pays a
 * match win **3**, a tie 1 each and a loss 0; its knockout stage pays 2/1/0, as
 * does every other event in the goldens. Until #712's served team rows land,
 * this browser-side table is what a visitor reads, so the scale has to be the
 * league's and not a convention: on the committed 2026 slice Lichess's own
 * table pays 9/6/6/6/6/3, which is unreachable from a 2-per-win rule.
 *
 * It is config for the same reason `SCORING` is — one site, many events — and
 * `standings.test.ts` reads the canonical rules file back out and fails if the
 * default and the engine ever disagree.
 */
// mirror-of: data-curated/standings-goldens/gcl-2026/engine-rules.json
export const MATCH_POINTS: MatchScoring = parseMatchPoints(env("VITE_MATCH_POINTS", "3,1,0"));

function parseMatchPoints(raw: string): MatchScoring {
  const parts = raw.split(",").map((p) => Number.parseFloat(p.trim()));
  const [w, d, l] = parts;
  if (parts.length !== 3 || w === undefined || d === undefined || l === undefined || !Number.isFinite(w) || !Number.isFinite(d) || !Number.isFinite(l)) {
    console.warn(`[config] VITE_MATCH_POINTS="${raw}" is not "win,draw,loss" — falling back to the GCL round-robin scale`);
    return { win: 3, draw: 1, loss: 0 };
  }
  return { win: w, draw: d, loss: l };
}

/**
 * Tours whose scoring IS the configured scale, `VITE_SCORING_TOURS="id,id"`.
 *
 * `SCORING` / `MATCH_POINTS` are one site-wide scale, but the site lists every
 * tournament on the wire, and the Olympiad (or the GCL Finals, 1/1/½) does not
 * pay 3/4/1. Until per-tournament scoring rules are on the wire, the round
 * page's board-points bar renders ONLY on these tours and is hidden elsewhere
 * — no default scale is guessed. Unlike `VITE_TOUR_ID`, an empty list means
 * "score nothing". The default is the GCL 2026 round robin, the tour the
 * canonical rules file names; `standings.test.ts` checks every id against it.
 */
// mirror-of: data-curated/standings-goldens/gcl-2026/engine-rules.json
export const SCORING_TOURS: readonly string[] = parseTourIds(env("VITE_SCORING_TOURS", "B6gJ5CBc"));

/** Human-readable scale, for the label under the computed table. */
export const scoringLabel = (s: BoardScoring = SCORING): string =>
  `win ${s.whiteWin} with White · ${s.blackWin} with Black · draw ${s.draw}`;

/** Human-readable match scale, the one definition of those words. */
export const matchPointsLabel = (m: MatchScoring = MATCH_POINTS): string =>
  `${m.win} for a match win, ${m.draw} each on a tie`;

/**
 * The official table's address for THIS deploy, `VITE_OFFICIAL_STANDINGS_URL`.
 *
 * A FALLBACK, never the first answer: `officialStandingsLink` reads the
 * tournament's own `TournamentInfo.standings` first and only consults this when
 * the broadcast published none. That case is real rather than theoretical —
 * the field reaches a browser only over `Broadcast.ListRounds`, which is empty
 * in worker mode (issue 1803), the mode staging runs in — and the orchestrator's
 * ruling is that the link must not vanish for the live event. Linking OUT to an
 * organiser's table is not republishing their data (issue 730 Phase 1); nothing
 * from that site is rendered here.
 *
 * **The default is EMPTY and must stay empty.** An event's address is DEPLOY
 * configuration, documented in this site's README environment table and in
 * `scripts/deploy-pages.sh`'s environment block; compiled into this source it
 * would be wrong for every other event the same bundle serves and would outlive
 * the event it was typed for. `standings.test.ts` fails if this default is ever
 * given a value, and reads the rendering sources back to catch a literal
 * reappearing at a call site.
 *
 * The durable fix retires this row: project `TournamentInfo.standings` onto
 * `BroadcastEntry`, which the calendar already receives, and the field is
 * always there. That is a proto change, filed for the S3-web lane.
 */
export const OFFICIAL_STANDINGS_URL: string = env("VITE_OFFICIAL_STANDINGS_URL", "");

/**
 * Email capture is legally gated (#2064): under PECR/GDPR an address may only be
 * collected for launch news with a published privacy policy and explicit,
 * separate consent. The policy lives in PR #1807 and is not merged, so the
 * default here is OFF and the CTA strip shows an "app coming soon" line only.
 *
 * Turning it on takes BOTH `VITE_WAITLIST_ENABLED=1` and a `VITE_PRIVACY_URL`
 * that resolves to the published policy — the form fails closed without the
 * second, because consent that cannot be read is not consent.
 *
 * **The default stays empty even though `/privacy` now renders.** The site
 * hosts the policy, but the text is PR #1807's draft and still carries seven
 * unfilled placeholders — the privacy contact address, the GDPR Art 27 EU
 * representative, the ICO registration number, the OTP lifetime, the two
 * provider regions and the effective date. A policy with no contact address and
 * no effective date is not a published policy, and consent taken against it is
 * not consent, so the gate stays closed. `src/legal/documents.ts` decides this
 * from the document itself (`isDraft`), and `legal-gate.test.ts` fails if this
 * default is ever pointed at a document that is still a draft.
 */
export const PRIVACY_URL = env("VITE_PRIVACY_URL", "");

/** Optional "follow us" destination shown while the waitlist is off. Unset ⇒ omitted. */
export const FOLLOW_URL = env("VITE_FOLLOW_URL", "");

export const WAITLIST_ENABLED: boolean = (() => {
  if (env("VITE_WAITLIST_ENABLED", "") !== "1") return false;
  if (PRIVACY_URL.length === 0) {
    console.warn("[config] VITE_WAITLIST_ENABLED=1 but VITE_PRIVACY_URL is empty — email capture stays OFF (#2064)");
    return false;
  }
  return true;
})();

/**
 * Player portraits are ON by default (owner decision 2026-09-17, recorded in
 * `docs/legal/player-photo-licensing-due-diligence.md` §Owner decision).
 *
 * Attribution is served by `/credits`: `public/credits/photo-credits.json`
 * (built by `scripts/build-photo-credits.mjs` from the player-photos
 * catalogue) carries all 4,058 per-photo entries — author, credit, source
 * links, licence, Commons file page — which CC BY / BY-SA accept as
 * attribution in a reasonable manner for the medium (a linked credits page),
 * so no per-photo caption is required where a portrait renders.
 *
 * `VITE_PLAYER_PHOTOS=0` is the build-time kill switch — exactly `0`, so a
 * typo cannot turn photos off by accident. Per player, an empty
 * `avatar_r2_url` still renders the initials monogram. The CSP in
 * `public/_headers` carries the R2 image host unconditionally.
 */
export const PLAYER_PHOTOS: boolean = env("VITE_PLAYER_PHOTOS", "") !== "0";

/**
 * The credit link under the player page's large portrait, pointing at the
 * per-photo attribution on `/credits`.
 */
export const PHOTO_CREDIT = "Photo: Wikimedia Commons — see credits";

/**
 * Where player photos live on the object plane: `<base>/<fide_N>.jpg`
 * (`avatarUrl` in `ui/avatar.ts`). The iOS app derives the same base as its
 * object plane plus `/avatars` (`ServerEnvironment.avatarBase`); the web's
 * object plane is the R2 Worker host already allowed by `img-src` in
 * `public/_headers`. Photos still paint only when {@link PLAYER_PHOTOS} is on.
 */
export const PLAYER_AVATAR_BASE = env("VITE_PLAYER_AVATAR_BASE", "https://objects-staging.arrowchess.com/o/avatars").replace(/\/+$/, "");

/**
 * The published Chroma theme catalog (`docs/theme/theme-catalog-as-data-2026-09.md` §2.2): one
 * JSON array of theme documents on the same object plane as {@link PLAYER_AVATAR_BASE}, so its
 * origin is already in `connect-src` (`config.csp.test.ts` pins that). The bundled
 * `web/tokens/themes.json` paints first; `ui/theme.ts` then fetches this and, when it validates,
 * replaces the list — adding or removing a theme is a data change, not a build.
 */
export const THEME_CATALOG_URL = env("VITE_THEME_CATALOG_URL", "https://objects-staging.arrowchess.com/o/themes/v1/catalog.json");

/**
 * The origins a catalog `BroadcastEntry.image_url` (the tour cover art) can point at. The URL is
 * Lichess's signed thumbnail, passed through byte-for-byte, so its host is Lichess's choice, not
 * ours. Measured 2026-11-01: the served `calendar.pb` carries 679 `image.lichess.org` URLs and 0
 * `image.lichess1.org`, and `image.lichess1.org` now answers 301 to `image.lichess.org` — a CSP
 * checks the redirect target too, so allowing only the old host blocked every cover (#3708).
 * `image.lichess1.org` stays for catalog rows minted before the move. `config.csp.test.ts` pins
 * every entry here into `img-src` in `public/_headers`.
 */
export const COVER_IMAGE_ORIGINS: readonly string[] = ["https://image.lichess.org", "https://image.lichess1.org"];

// ---- local analysis engine (#2076 follow-up) --------------------------------

/**
 * The in-browser engine, served from our own origin — see
 * `public/engine/NOTICE.md` for the build, the bytes and the licence.
 *
 * The `.wasm` sits beside the `.js` under the same basename because that is how
 * the emscripten glue finds it: inside a worker it defaults the wasm URL to its
 * own `location.pathname.replace(/\.js$/i, ".wasm")`. Renaming one file without
 * the other produces a 404 the engine reports as "wasm streaming compile
 * failed", so the two names are one fact and this constant is where it lives.
 */
export const LOCAL_ENGINE_URL = env("VITE_LOCAL_ENGINE_URL", "/engine/stockfish-19.0.0-lite-single-57ac2d72.js");

/** Short name for the badge on a locally-computed eval. */
export const LOCAL_ENGINE_NAME = env("VITE_LOCAL_ENGINE_NAME", "sf19");

/**
 * How the engine introduces itself on the panel, and what it costs to say yes.
 *
 * Both are here rather than in the panel because they are facts about the
 * SHIPPED BYTES: change `LOCAL_ENGINE_URL` and these two must move with it, and
 * a size quoted in the UI that no longer matches the file is a small lie told
 * at exactly the moment the visitor is deciding whether to trust the feature.
 * The authority for both is `public/engine/NOTICE.md`, which records the build
 * and the byte counts, and `scripts/fetch-web-engine.sh`, which re-derives them.
 */
export const LOCAL_ENGINE_LABEL = "Stockfish 19 (lite) in your browser";

/** Rounded up from the 1,808,986 B actually served (glue + wasm). */
export const LOCAL_ENGINE_MB = 2;

/**
 * Depth cap for one local search, `VITE_LOCAL_ENGINE_DEPTH`.
 *
 * 18 is the trade this site wants: on the lite single-threaded net it lands in
 * roughly a second on a laptop and a few on a phone, which is inside the window
 * a spectator will wait after clicking a move, and it is deep enough that the
 * bar stops flipping sign between plies. It is a cap, not a target — the search
 * is stopped early whenever the position changes.
 */
export const LOCAL_ENGINE_DEPTH = clampInt(env("VITE_LOCAL_ENGINE_DEPTH", "18"), 18, 1, 40);

/**
 * The MultiPV range the engine is ever asked for, and the ONE definition of it.
 *
 * Three places need this bound and they must not each invent it: the build-time
 * default below, the 1..5 selector on the panel, and the clamp applied to the
 * value read back out of `localStorage` (which is typo- and attacker-writable).
 * 5 matches the Lichess analysis board's ceiling; past it the extra lines are
 * noise and each one costs real search time in a spectator's tab.
 */
export const LOCAL_ENGINE_MULTIPV_MIN = 1;
export const LOCAL_ENGINE_MULTIPV_MAX = 5;

/** How many lines the engine reports (`MultiPV`) on first load; the panel can change it live. */
export const LOCAL_ENGINE_MULTIPV = clampInt(
  env("VITE_LOCAL_ENGINE_MULTIPV", "2"),
  2,
  LOCAL_ENGINE_MULTIPV_MIN,
  LOCAL_ENGINE_MULTIPV_MAX,
);

/** Transposition table, MiB. Small on purpose: this runs in a spectator's tab. */
export const LOCAL_ENGINE_HASH_MB = clampInt(env("VITE_LOCAL_ENGINE_HASH_MB", "16"), 16, 1, 256);

/**
 * Quiet period before a position change starts a search, ms. A scrub through a
 * move list is a burst of position changes; without this every intermediate ply
 * would get its own `go`, and the engine would spend the whole scrub starting
 * and stopping. `stop` is still sent immediately — only the `go` waits.
 */
export const LOCAL_ENGINE_DEBOUNCE_MS = clampInt(env("VITE_LOCAL_ENGINE_DEBOUNCE_MS", "250"), 250, 0, 5000);

/**
 * A `VITE_*` integer tunable, or the documented default.
 *
 * The whole value must be an integer, which is why the shape is tested before
 * `parseInt` rather than after: `parseInt` is PREFIX-lenient, so it reads
 * `"18.5"` as 18 and `"16MB"` as 16 and the build silently ships a number
 * nobody wrote. A depth of 18 that the operator meant as 18.5 is harmless; a
 * hash of 16 MiB that the operator wrote as `"16000MB"` is a 1000× miss taken
 * in silence. A malformed value is a mistake to report, not to interpret — so
 * anything that is not a plain (optionally signed) run of digits, modulo
 * surrounding whitespace, warns and falls back.
 *
 * The warning is suppressed when the raw value IS the fallback spelled out,
 * because a default re-stated in the environment is not a misconfiguration.
 */
export function clampInt(raw: string, fallback: number, lo: number, hi: number): number {
  const trimmed = raw.trim();
  const warn = () => {
    if (raw !== String(fallback)) console.warn(`[config] "${raw}" is not an integer in [${lo}, ${hi}] — using ${fallback}`);
  };
  if (!/^[+-]?\d+$/u.test(trimmed)) {
    warn();
    return fallback;
  }
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(n) || n < lo || n > hi) {
    warn();
    return fallback;
  }
  return n;
}
